import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { can, AvailableResourceActions } from '@marsinet/commons';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import type { VideoItem } from '../videos';

const getTagStyle = (hexColor?: string): React.CSSProperties => {
  const color = hexColor || '#3B82F6';
  return {
    backgroundColor: color,
    borderColor: color,
    color: '#ffffff',
  };
};

interface VideoEditTagsSimpleModalProps {
  visible: boolean;
  onHide: () => void;
  video: VideoItem | null;
  onTagsUpdated?: (updatedVideo: VideoItem) => void;
}

const VideoEditTagsSimpleModal: React.FC<VideoEditTagsSimpleModalProps> = ({
  visible,
  onHide,
  video,
  onTagsUpdated,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { role } = useContext(MarsinetContext);
  const canManageTags =
    !!role && can(role, AvailableResourceActions.videos.manage_tags);

  // State
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagColors, setTagColors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const tagButtonStyle: React.CSSProperties = {
    // width: '170px',
    // minWidth: '170px',

    backgroundColor: '#13dfb6',
    borderColor: '#13dfb6',
    color: '#ffffff',
    marginLeft: 5,
  };

  // Load available tags
  const loadAvailableTags = async () => {
    if (!video) return;

    setLoadingTags(true);
    try {
      const response =
        (await apiServices.videosApi.videosControllerGetAllTags()) as any;
      const tags = response.data.tags || [];
      const tagNames = tags.map((tag: any) => tag.name);
      const colorsMap = Object.fromEntries(
        tags.map((tag: any) => [tag.name, tag.color || '#3B82F6'])
      );
      setAvailableTags(tagNames);
      setTagColors(colorsMap);
    } catch (error) {
      console.error('Error loading available tags:', error);
      showToast(
        'error',
        t('common.error'),
        t('common.error_loading_tags'),
        2000
      );
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    if (visible && video) {
      loadAvailableTags();
      // Set current video tags as selected
      setSelectedTags(video.tags || []);
    }
  }, [visible, video]);

  const handleSaveTags = async () => {
    if (!video || !canManageTags) return;

    try {
      setLoading(true);
      const tagsQuery = selectedTags.join(',');

      await apiServices.videosApi.videosControllerUpdateVideoTags(
        video.id,
        tagsQuery
      );

      showToast(
        'success',
        t('common.success'),
        selectedTags.length > 0
          ? t('common.video_tags_updated')
          : t('common.video_tags_removed'),
        2000
      );

      // Update parent with the updated video
      const updatedVideo: VideoItem = {
        ...video,
        tags: selectedTags.length > 0 ? selectedTags : [],
      };
      onTagsUpdated?.(updatedVideo);
      onHide();
    } catch (error) {
      console.error('Error updating video tags:', error);
      showToast(
        'error',
        t('common.error'),
        t('common.error_updating_video_tags'),
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  if (!video || !canManageTags) return null;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={
        <div className="!flex !flex-col w-full gap-1">
          <div className="font-semibold">
            🎬 {t('common.edit_video_tags_title')}
          </div>
          <div className="text-sm text-gray-600">
            {video.title || video.filename}
          </div>
        </div>
      }
      modal
      style={{ width: '90%', maxWidth: '600px' }}
      className="p-fluid"
    >
      <div className="space-y-6">
        {/* Información del video */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-base text-blue-900">
            {t('common.edit_video_tags_help')}
          </p>
        </div>

        {loadingTags ? (
          <div className="flex justify-center items-center p-6">
            <ProgressSpinner
              style={{ width: '50px', height: '50px' }}
              strokeWidth="4"
            />
          </div>
        ) : (
          <>
            {/* Selector de tags */}
            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <label className="text-base block text-sm font-medium mb-2 text-gray-900">
                {t('common.available_tags', { total: availableTags.length })}
              </label>
              {availableTags.length === 0 ? (
                <p className="text-base text-gray-500 text-sm italic">
                  {t('common.no_tags_available')}
                </p>
              ) : (
                <MultiSelect
                  value={selectedTags}
                  onChange={(e) => setSelectedTags(e.value)}
                  options={availableTags}
                  placeholder={t('common.select_tags_for_video')}
                  className="w-full"
                  display="chip"
                  showSelectAll={true}
                />
              )}
            </div>

            {/* Preview */}
            {selectedTags.length > 0 && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <label className="text-base block text-sm font-medium mb-2 text-green-900">
                  {t('common.selected_tags')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map((tag) => (
                    <Tag
                      key={tag}
                      value={tag}
                      style={getTagStyle(tagColors[tag])}
                    />
                  ))}
                </div>
                <small className="text-base text-green-700 mt-2 block">
                  {t('common.tags_selected_count', {
                    selected: selectedTags.length,
                  })}
                </small>
              </div>
            )}

            {selectedTags.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-base text-gray-600">
                  {t('common.no_tags_selected')}
                </p>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <Button
                label={t('common.cancel')}
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-outlined p-button-sm"
                disabled={loading}
              />
              <Button
                label={t('common.save_tags')}
                icon="pi pi-check"
                onClick={handleSaveTags}
                loading={loading}
                className="p-button-sm"
                style={tagButtonStyle}
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default VideoEditTagsSimpleModal;